import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X, Calendar } from 'lucide-react';

export default function ExpeditionCalendarModal({
  isOpen,
  onClose,
  currentDate,
  onSelectDate,
  availableDates = []
}) {
  const currentDateObj = currentDate instanceof Date ? currentDate : (currentDate ? new Date(currentDate) : new Date());

  const [viewYear, setViewYear] = useState(() => currentDateObj.getFullYear());
  const [viewMonth, setViewMonth] = useState(() => currentDateObj.getMonth());
  const [monthCounts, setMonthCounts] = useState({});

  useEffect(() => {
    if (isOpen) {
      const d = currentDate instanceof Date ? currentDate : (currentDate ? new Date(currentDate) : new Date());
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    }
  }, [isOpen, currentDate]);

  useEffect(() => {
    if (!isOpen) return;
    let isCancelled = false;

    const fetchMonthCounts = async () => {
      try {
        const monthStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;
        const res = await fetch(`/api/database/calendar-counts?month=${monthStr}`);
        if (res.ok) {
          const data = await res.json();
          if (!isCancelled) {
            setMonthCounts(data.stops || {});
          }
        }
      } catch (e) {
        console.warn('Error loading calendar counts:', e);
      }
    };

    fetchMonthCounts();
    return () => { isCancelled = true; };
  }, [isOpen, viewYear, viewMonth]);

  if (!isOpen) return null;

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];
  const weekDayHeaders = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(prev => prev - 1);
    } else {
      setViewMonth(prev => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(prev => prev + 1);
    } else {
      setViewMonth(prev => prev + 1);
    }
  };

  const handleToday = () => {
    const today = new Date();
    onSelectDate(today);
    onClose();
  };

  const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
  const lastDayOfMonth = new Date(viewYear, viewMonth + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();

  let firstDayOfWeek = firstDayOfMonth.getDay() - 1;
  if (firstDayOfWeek === -1) firstDayOfWeek = 6;

  const days = [];
  const prevMonthLastDay = new Date(viewYear, viewMonth, 0).getDate();
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    days.push({ day: prevMonthLastDay - i, isCurrentMonth: false, isPrev: true });
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({ day: i, isCurrentMonth: true });
  }
  const remainingCells = 42 - days.length;
  for (let i = 1; i <= remainingCells; i++) {
    days.push({ day: i, isCurrentMonth: false, isNext: true });
  }

  const selectedIso = `${currentDateObj.getFullYear()}-${String(currentDateObj.getMonth() + 1).padStart(2, '0')}-${String(currentDateObj.getDate()).padStart(2, '0')}`;
  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  return (
    <div
      className="modal-overlay"
      style={{ zIndex: 2200 }}
      onClick={onClose}
    >
      <div
        className="modal-content"
        style={{
          width: '390px',
          background: '#0f172a',
          border: '1px solid #334155',
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 25px rgba(56, 189, 248, 0.1)',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Calendar size={18} color="#38bdf8" />
            <h3 style={{ margin: 0, fontSize: '0.96rem', fontWeight: '800', color: '#ffffff' }}>
              Date d'expédition
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#94a3b8',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Month Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1e293b', padding: '7px 12px', borderRadius: '10px', border: '1px solid #334155' }}>
          <button
            type="button"
            onClick={handlePrevMonth}
            style={{ background: 'transparent', border: 'none', color: '#cbd5e1', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
            title="Mois précédent"
          >
            <ChevronLeft size={18} />
          </button>

          <div style={{ fontWeight: '700', fontSize: '0.9rem', color: '#ffffff' }}>
            {monthNames[viewMonth]} {viewYear}
          </div>

          <button
            type="button"
            onClick={handleNextMonth}
            style={{ background: 'transparent', border: 'none', color: '#cbd5e1', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
            title="Mois suivant"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Weekday headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', textAlign: 'center' }}>
          {weekDayHeaders.map(w => (
            <span key={w} style={{ fontSize: '0.7rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>
              {w}
            </span>
          ))}
        </div>

        {/* Days grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
          {days.map((dObj, idx) => {
            let itemYear = viewYear;
            let itemMonth = viewMonth;
            if (dObj.isPrev) {
              if (itemMonth === 0) { itemMonth = 11; itemYear--; }
              else { itemMonth--; }
            } else if (dObj.isNext) {
              if (itemMonth === 11) { itemMonth = 0; itemYear++; }
              else { itemMonth++; }
            }
            const dateIso = `${itemYear}-${String(itemMonth + 1).padStart(2, '0')}-${String(dObj.day).padStart(2, '0')}`;
            const isSelected = dateIso === selectedIso;
            const isToday = dateIso === todayIso;

            const stopCount = monthCounts[dateIso] !== undefined
              ? monthCounts[dateIso]
              : (availableDates.find(ad => ad.date === dateIso)?.count || 0);

            const hasDeliveries = stopCount > 0;

            return (
              <button
                key={idx}
                type="button"
                onClick={() => {
                  const [y, m, d] = dateIso.split('-').map(Number);
                  onSelectDate(new Date(y, m - 1, d));
                  onClose();
                }}
                style={{
                  minHeight: '40px',
                  background: isSelected
                    ? 'rgba(56, 189, 248, 0.25)'
                    : isToday
                      ? 'rgba(255, 255, 255, 0.06)'
                      : hasDeliveries
                        ? 'rgba(34, 197, 94, 0.08)'
                        : 'transparent',
                  border: isSelected
                    ? '2px solid #38bdf8'
                    : isToday
                      ? '1px solid #94a3b8'
                      : hasDeliveries
                        ? '1px solid rgba(34, 197, 94, 0.3)'
                        : '1px solid transparent',
                  borderRadius: '8px',
                  color: dObj.isCurrentMonth ? '#f8fafc' : '#475569',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '1px',
                  padding: '3px 1px',
                  transition: 'all 0.12s ease'
                }}
              >
                <span style={{ fontSize: '0.8rem', fontWeight: isSelected || isToday ? '800' : '600' }}>
                  {dObj.day}
                </span>

                {hasDeliveries && (
                  <span
                    style={{
                      background: isSelected ? '#38bdf8' : '#22c55e',
                      color: '#0f172a',
                      fontSize: '0.6rem',
                      fontWeight: '800',
                      padding: '0 4px',
                      borderRadius: '8px',
                      lineHeight: '1.2',
                      minWidth: '15px',
                      textAlign: 'center'
                    }}
                    title={`${stopCount} livraison${stopCount > 1 ? 's' : ''}`}
                  >
                    {stopCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Footer with Today Shortcut */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid #334155' }}>
          <button
            type="button"
            onClick={handleToday}
            style={{
              background: 'rgba(56, 189, 248, 0.15)',
              color: '#38bdf8',
              border: '1px solid #38bdf8',
              borderRadius: '8px',
              padding: '5px 12px',
              fontSize: '0.76rem',
              fontWeight: '700',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            Aujourd'hui
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.72rem', color: '#94a3b8' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', display: 'inline-block' }}></span>
            <span>Livraisons disponibles</span>
          </div>
        </div>
      </div>
    </div>
  );
}
